/**
 * TanStack Query hooks for the OR-6126 concept-group preview review surface.
 *
 * The chat chip carries a ``previewId``; the review page (right pane) reads the
 * preview's rules + flat concept list and (on Create) confirms it into the org library.
 * All reads/writes go through the feature's ``authFetch`` (TanStack), NOT the
 * legacy ``data-access-concept-groups`` RTK slice we're migrating off — the
 * preview endpoints live at ``/concept-groups/preview/...`` (same path the
 * sibling ``prevalenceQueries`` TanStack module already uses).
 *
 * After a successful Create the chat resumes server-side (the REST confirm
 * handler kicks the originating session), so these hooks emit no socket event.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl, getGatewayApiVersion } from '@verantos-dev/core-constants';
import type { MappingResult } from '@verantos-dev/feature-code-mapping';

import { authFetch } from './authFetch';
import { conceptGroupStatusKeys } from './conceptGroupStatusQueries';

const CONCEPT_GROUPS_PATH = '/concept-groups';

// --- Schemas (parse at the HTTP trust boundary) ----------------------------

const ruleItemSchema = z.object({
  conceptId: z.union([z.string(), z.number()]),
  conceptName: z.string().nullish(),
  vocabularyId: z.string().nullish(),
  includeDescendants: z.boolean(),
  includeMapped: z.boolean(),
  isExcluded: z.boolean(),
});

const previewDetailSchema = z.object({
  previewId: z.string(),
  conceptGroupName: z.string(),
  membershipMode: z.string().nullish(),
  projectId: z.string().nullish(),
  status: z.string().nullish(),
  createdConceptGroupId: z.string().nullish(),
  domains: z.array(z.string()).default([]),
  totalCount: z.number().default(0),
  rationale: z.string().nullish(),
  ruleItems: z.array(ruleItemSchema).default([]),
  // OR-6125: true for a map-first upload (the review surface shows a "Mapping results" tab
  // backed by the stored source→mapped detail). False for direct id uploads / proposals.
  hasMappingDetail: z.boolean().default(false),
  // OR-6288 W3: original uploaded filename (pointer; bytes stream on demand).
  sourceFilename: z.string().nullable().default(null),
  // OR-6288 W6: keys the stored raw file for the re-download row.
  uploadId: z.string().nullable().default(null),
  // OR-6288 Stage C CAS: the draft revision this view loaded with. Threaded back
  // as ``expected_rev`` on confirm so publishing a draft that moved since is
  // refused (HTTP 409) instead of overwriting a definition the researcher never
  // saw. Backend sends it (``get_preview_detail`` → ``draftRev=preview['rev']``);
  // legacy previews without a rev return null (CAS check then skipped).
  draftRev: z.number().nullish(),
  // OR-6125 review: raw entries from a direct id upload that weren't valid concept ids and were
  // skipped — shown (red) on the review surface. Empty for map-first uploads and proposals.
  invalidValues: z.array(z.string()).default([]),
});

const previewDetailEnvelopeSchema = z.object({ data: previewDetailSchema });

const previewConceptSchema = z.object({
  conceptId: z.union([z.string(), z.number()]),
  conceptName: z.string().nullish(),
  vocabularyId: z.string().nullish(),
  conceptCode: z.string().nullish(),
  domainId: z.string().nullish(),
  standardConcept: z.string().nullish(),
});

const previewConceptsEnvelopeSchema = z.object({
  data: z.array(previewConceptSchema).default([]),
  pagination: z
    .object({
      total: z.number().default(0),
      count: z.number().default(0),
      perPage: z.number().default(0),
      currentPage: z.number().default(1),
    })
    .partial()
    .default({}),
});

// --- Mapping detail (OR-6125: source→mapped review for a map-first upload) --
//
// The backend returns the hierarchical Source/Mapped detail (research-assistant
// `MappingResult` model dump): a Source row per uploaded code, with nested
// `mappedRows` for its standard target(s), plus not-found / unmapped rows. We
// reuse the legacy `MappingResultsGrid`, whose row type uses `class`; the backend
// dumps `conceptClass`. Parse the raw shape, then map field-for-field to the grid's
// `MappingResult` (renaming `conceptClass` → `class`, recursively).

interface RawMappingRow {
  type: 'Source' | 'Mapped' | 'Both';
  id: string;
  name: string;
  code: string;
  vocabulary: string;
  standard: string;
  domain: string;
  conceptClass: string;
  status: string;
  mappedCount: number | string;
  reasoning: string;
  isCollapsible?: boolean | null;
  mappedRows?: RawMappingRow[] | null;
}

const rawMappingRowSchema: z.ZodType<RawMappingRow> = z.lazy(() =>
  z.object({
    type: z.enum(['Source', 'Mapped', 'Both']),
    id: z.string().default(''),
    name: z.string().default(''),
    code: z.string().default(''),
    vocabulary: z.string().default(''),
    standard: z.string().default(''),
    domain: z.string().default(''),
    conceptClass: z.string().default(''),
    status: z.string().default(''),
    mappedCount: z.union([z.number(), z.string()]).default(''),
    reasoning: z.string().default(''),
    isCollapsible: z.boolean().nullish(),
    mappedRows: z.array(rawMappingRowSchema).nullish(),
  })
);

const mappingDetailSchema = z.object({
  results: z.array(rawMappingRowSchema).default([]),
});

const toMappingResult = (row: RawMappingRow): MappingResult => ({
  type: row.type,
  id: row.id,
  name: row.name,
  code: row.code,
  vocabulary: row.vocabulary,
  standard: row.standard,
  domain: row.domain,
  class: row.conceptClass,
  status: row.status,
  mappedCount: row.mappedCount,
  reasoning: row.reasoning,
  isCollapsible: row.isCollapsible ?? undefined,
  mappedRows: row.mappedRows ? row.mappedRows.map(toMappingResult) : undefined,
});

const isValidNameEnvelopeSchema = z.object({ isValid: z.boolean() });

const confirmEnvelopeSchema = z.object({
  data: z.object({ conceptGroupId: z.string(), conceptGroupName: z.string().nullish() }),
});

export type ConceptGroupPreviewRuleItem = z.infer<typeof ruleItemSchema>;
export type ConceptGroupPreviewDetail = z.infer<typeof previewDetailSchema>;
export type PreviewConcept = z.infer<typeof previewConceptSchema>;

export type PreviewStandardFilter = 'all' | 'standard' | 'non_standard';

export interface PreviewConceptsResult {
  concepts: PreviewConcept[];
  total: number;
}

// --- Query keys ------------------------------------------------------------

export const conceptGroupPreviewKeys = {
  /** Prefix-match key covering every sub-query below (detail/concepts/mapping)
   *  in one invalidation — see `resourceQueryKeys.ts`'s `concept_group` case. */
  prefix: (previewId: string) => ['concept-group-preview', previewId] as const,
  detail: (previewId: string) => ['concept-group-preview', previewId, 'detail'] as const,
  concepts: (
    previewId: string,
    page: number,
    pageSize: number,
    search: string,
    std: PreviewStandardFilter,
    sort: string,
    filter: string
  ) =>
    [
      'concept-group-preview',
      previewId,
      'concepts',
      page,
      pageSize,
      search,
      std,
      sort,
      filter,
    ] as const,
  mapping: (previewId: string) => ['concept-group-preview', previewId, 'mapping'] as const,
  /** Keyed by name, not previewId — the verdict is a property of the name. */
  nameAvailability: (name: string, projectId?: string) =>
    ['concept-group-name-availability', name, projectId ?? null] as const,
};

// --- Hooks -----------------------------------------------------------------

/** Standalone fetcher so post-upload flows can PREFETCH the review's data
 *  before navigating to it (no skeleton flash on arrival). */
export const fetchConceptGroupPreviewDetail = async (
  previewId: string
): Promise<ConceptGroupPreviewDetail> => {
  const json = await authFetch<unknown>(
    `${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(previewId)}`
  );
  return previewDetailEnvelopeSchema.parse(json).data;
};

/** Preview metadata + per-seed rules for the read-only Rules tab. */
export const useConceptGroupPreview = (previewId: string | null) =>
  useQuery({
    queryKey: conceptGroupPreviewKeys.detail(previewId ?? ''),
    enabled: Boolean(previewId),
    queryFn: () => fetchConceptGroupPreviewDetail(previewId ?? ''),
  });

const standardFilterParam = (std: PreviewStandardFilter): string | null => {
  if (std === 'standard') return 'standardConcept:eq(S)';
  if (std === 'non_standard') return 'standardConcept:ne(S)';
  return null;
};

interface UsePreviewConceptsArgs {
  previewId: string | null;
  page?: number; // 1-based
  pageSize?: number;
  search?: string;
  standardFilter?: PreviewStandardFilter;
  /** Serialized grid sort, e.g. ``conceptName:asc``. */
  sort?: string;
  /** Serialized grid column filter, e.g. ``conceptName:contains(foo)``. */
  filter?: string;
}

/** A page of the flat materialized concept list, with search + standard filter. */
export const usePreviewConcepts = ({
  previewId,
  page = 1,
  pageSize = 50,
  search = '',
  standardFilter = 'all',
  sort = '',
  filter = '',
}: UsePreviewConceptsArgs) =>
  useQuery({
    queryKey: conceptGroupPreviewKeys.concepts(
      previewId ?? '',
      page,
      pageSize,
      search.trim(),
      standardFilter,
      sort,
      filter
    ),
    enabled: Boolean(previewId),
    // Keep the prior page visible while the next loads so paging / typing
    // doesn't flash an empty table.
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<PreviewConceptsResult> => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      // The preview endpoint expresses the standard toggle as a ``filter``
      // clause; column filters are additional clauses AND-ed onto it, so merge
      // both into the single comma-separated ``filter`` param.
      const clauses = [standardFilterParam(standardFilter), filter.trim()].filter(Boolean);
      if (clauses.length) params.set('filter', clauses.join(','));
      if (sort) params.set('sort', sort);
      const term = search.trim();
      if (term) params.set('search', term);
      const json = await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(
          previewId ?? ''
        )}/concepts?${params.toString()}`
      );
      const parsed = previewConceptsEnvelopeSchema.parse(json);
      return { concepts: parsed.data, total: parsed.pagination.total ?? 0 };
    },
  });

/**
 * Source→mapped detail for a map-first upload (OR-6125). Returns the hierarchical
 * `MappingResult[]` the legacy `MappingResultsGrid` renders — Source rows with nested
 * mapped targets, plus not-found / unmapped rows. Only enabled when the preview has a
 * stored mapping artifact (`detail.hasMappingDetail`); the endpoint 404s otherwise.
 */
export const usePreviewMapping = (previewId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: conceptGroupPreviewKeys.mapping(previewId ?? ''),
    enabled: Boolean(previewId) && enabled,
    queryFn: async (): Promise<MappingResult[]> => {
      const json = await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(previewId ?? '')}/mapping`
      );
      return mappingDetailSchema.parse(json).results.map(toMappingResult);
    },
  });

/**
 * Download the original file the researcher uploaded into the chat (OR-6125). Keyed by
 * `uploadId` (not a preview), so the uploaded-file chip is downloadable even for an
 * unrecognized file that never minted a preview. Streams the raw bytes (not JSON), so —
 * like {@link downloadPreviewCsv} — it does an authenticated raw fetch and triggers a
 * browser download via a transient anchor.
 */
export const downloadUploadSource = async (uploadId: string, fileName: string): Promise<void> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/uploads/${encodeURIComponent(
    uploadId
  )}/source`;
  const response = await fetch(url, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(`Source download failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName || 'concept-group-upload';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** Duplicate-name check for the Create action (reuses the shared validator endpoint). */
export const useValidatePreviewName = () =>
  useMutation({
    mutationFn: async (args: {
      conceptGroupName: string;
      projectId?: string;
    }): Promise<boolean> => {
      const json = await authFetch<unknown>(`${CONCEPT_GROUPS_PATH}/create/is-valid-name`, {
        method: 'POST',
        body: JSON.stringify(args),
      });
      return isValidNameEnvelopeSchema.parse(json).isValid;
    },
  });

/**
 * The same duplicate-name check as a cached read, for a name the researcher is not
 * typing — the Draft review surface warns on open instead of waiting for Approve.
 */
export const useConceptGroupNameAvailability = ({
  name,
  projectId,
  enabled = true,
}: {
  name: string;
  projectId?: string;
  enabled?: boolean;
}) => {
  const conceptGroupName = name.trim();
  return useQuery({
    queryKey: conceptGroupPreviewKeys.nameAvailability(conceptGroupName, projectId),
    enabled: enabled && Boolean(conceptGroupName),
    staleTime: 60_000,
    // Fails open: publish re-validates, so a broken check must not block Approve.
    retry: false,
    queryFn: async (): Promise<boolean> => {
      const json = await authFetch<unknown>(`${CONCEPT_GROUPS_PATH}/create/is-valid-name`, {
        method: 'POST',
        body: JSON.stringify({ conceptGroupName, ...(projectId ? { projectId } : {}) }),
      });
      return isValidNameEnvelopeSchema.parse(json).isValid;
    },
  });
};

/** Confirm a preview into a real org-library concept group. */
export const useConfirmPreview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      previewId: string;
      conceptGroupName: string;
      projectId?: string;
      /**
       * OR-6288 Stage C CAS: the draft rev this approval's view was based on. A
       * draft that moved since is refused with HTTP 409 (refresh + retry) instead
       * of publishing a definition the researcher never saw.
       */
      expectedRev?: number;
    }): Promise<string> => {
      const json = await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(args.previewId)}/confirm`,
        { method: 'POST', body: JSON.stringify(args) }
      );
      return confirmEnvelopeSchema.parse(json).data.conceptGroupId;
    },
    onSuccess: (conceptGroupId) => {
      // OR-6230: the CG just published (DynamoDB draft → Snowflake catalog). Invalidate
      // the inline chat card's status query (keyed on the same stable id) so it refetches
      // the now-published detail and the pill flips Draft → Ready — no poll, no backend
      // RESOURCE_UPDATED needed.
      queryClient.invalidateQueries({ queryKey: conceptGroupStatusKeys.detail(conceptGroupId) });
    },
  });
};

/**
 * Download the full preview concept list as CSV. The export endpoint streams a
 * file (not JSON), so this can't go through ``authFetch`` (which calls
 * ``.json()``); it does an authenticated raw fetch and triggers a browser
 * download via a transient anchor. Exports ALL concepts server-side, not just
 * the current page.
 */
export const downloadPreviewCsv = async (previewId: string, fileName: string): Promise<void> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(
    previewId
  )}/export`;
  const response = await fetch(url, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(`CSV export failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * OR-6125: upload a concept-group file in the chat. Multipart, so it can't go
 * through ``authFetch`` (which forces ``Content-Type: application/json`` and
 * would break the multipart boundary) — an authenticated raw fetch, letting the
 * browser set the boundary. The backend runs the upload as an agent turn and
 * emits the draft chip over the socket, so the response is a 202 with no body.
 */
export const uploadConceptGroupFile = async (args: {
  sessionId: string;
  projectId: string;
  file: File;
  name?: string;
}): Promise<void> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/preview/upload`;
  const form = new FormData();
  form.append('session_id', args.sessionId);
  form.append('project_id', args.projectId);
  if (args.name) {
    form.append('name', args.name);
  }
  form.append('file', args.file);
  const response = await fetch(url, {
    method: 'POST',
    // No Content-Type header — the browser sets multipart/form-data + boundary.
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!response.ok) {
    throw new Error(`Concept-group upload failed: ${response.status} ${response.statusText}`);
  }
};

/** Mutation wrapper for {@link uploadConceptGroupFile}. */
export const useUploadConceptGroupFile = () => useMutation({ mutationFn: uploadConceptGroupFile });

/** OR-6288 W6: synchronous page-driven upload — the pipeline completes in the
 *  call and returns the minted draft's ids so the page can redirect to review.
 *  ``sessionId`` is optional; when present the chip + card mirror into the chat.
 *  ``replacesPreviewId`` (UC-4, optional): the existing draft this re-upload
 *  supersedes — the backend discards it (soft-flip + RESOURCE_UPDATED) once the
 *  new draft is minted, so the project list keeps exactly one Draft row. */
export const uploadConceptGroupFileSync = async (args: {
  projectId: string;
  file: File;
  sessionId?: string | null;
  name?: string;
  replacesPreviewId?: string;
}): Promise<{ previewId: string; uploadId: string; conceptGroupId: string | null }> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/preview/upload-sync`;
  const form = new FormData();
  form.append('project_id', args.projectId);
  if (args.sessionId) {
    form.append('session_id', args.sessionId);
  }
  if (args.name) {
    form.append('name', args.name);
  }
  if (args.replacesPreviewId) {
    form.append('replaces_preview_id', args.replacesPreviewId);
  }
  form.append('file', args.file);
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!response.ok) {
    // Surface the server's actual error (FastAPI ``detail``) instead of a bare
    // status code — a 422 here carries a human message (e.g. "No usable OMOP
    // concepts were found in the file."). Falls back to the status when the body
    // isn't the expected shape.
    let detail = '';
    try {
      const errorBody = (await response.json()) as { detail?: string | { msg?: string }[] };
      if (typeof errorBody.detail === 'string') {
        detail = errorBody.detail;
      } else if (Array.isArray(errorBody.detail)) {
        detail = errorBody.detail
          .map((item) => item?.msg)
          .filter(Boolean)
          .join('; ');
      }
    } catch {
      // Non-JSON error body — fall through to the status message.
    }
    throw new Error(
      detail || `Concept-group upload failed: ${response.status} ${response.statusText}`
    );
  }
  const body = (await response.json()) as {
    data: { previewId: string; uploadId: string; conceptGroupId: string | null };
  };
  return body.data;
};

/** OR-6288 W4: download the generated review workbook for an agent draft
 *  (Definition + Concepts sheets; server builds lazily and caches by rev). */
export const downloadDraftExportXlsx = async (
  previewId: string,
  fileName: string
): Promise<void> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/preview/${encodeURIComponent(
    previewId
  )}/export.xlsx`;
  const response = await fetch(url, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(`Draft export failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

/** Mutation wrapper for {@link uploadConceptGroupFileSync}. */
export const useUploadConceptGroupFileSync = () =>
  useMutation({ mutationFn: uploadConceptGroupFileSync });
